import { useState } from 'react'
import {
  Box,
  type BoxProps,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SvgIcon,
  Typography
} from '@mui/material'
import parse from 'html-react-parser'

import Button from '@/components/base/button'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'

import { WPLink } from '@/types/wp'

export type PricingPlan = {
  title: string
  price: string
  features: {
    title: string
    description: string
  }[]
  highlighted?: boolean
  product_id?: number
  cities?: {
    title: string
  }[]
  button?: {
    link: WPLink
    icon?: string
  }
}

export type PricingGridProps = BoxProps &
  Partial<{
    pretitle: string
    title: string
    subtitle: string
    plans: PricingPlan[]
  }>

const PricingGrid = ({ pretitle, title, subtitle, plans, ...props }: PricingGridProps) => {
  const { strings } = useAppStore()
  const [selectedCities, setSelectedCities] = useState<Record<number, string>>({})

  return (
    <Box {...props}>
      <Container>
        {(pretitle || title || subtitle) && (
          <Grid container sx={{ mb: 6 }}>
            <Grid size={{ xs: 4, sm: 8, md: 8 }} offset={{ md: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  rowGap: 2,
                  textAlign: 'center'
                }}
              >
                {pretitle ? (
                  <Typography variant="overline" color="text.secondaryContrastText">
                    {pretitle}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography variant="h3" color="text.secondaryContrastText">
                    {title}
                  </Typography>
                ) : null}

                {subtitle ? (
                  <Typography variant="textRegular" color="text.primary">
                    {parse(subtitle)}
                  </Typography>
                ) : null}
              </Box>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={3}>
          {plans?.map((plan, index) => (
            <Grid key={`plan-${index}`} size={{ xs: 4, sm: 4, md: 3 }}>
              <Box
                sx={(theme) => ({
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: theme.shape.borderRadius4,
                  backgroundColor: plan.highlighted
                    ? theme.palette.primary.main
                    : theme.palette.surface.background,
                  color: plan.highlighted
                    ? theme.palette.text.primaryContrastText
                    : theme.palette.text.secondaryContrastText,
                  boxShadow: plan.highlighted ? theme.shadows[4] : theme.shadows[2],
                  transform: plan.highlighted ? 'scale(1.02)' : 'none',
                  position: 'relative',
                  zIndex: plan.highlighted ? 1 : 0
                })}
              >
                {/* Plan Title */}
                <Typography
                  fontSize="2.4rem"
                  letterSpacing="-0.02em"
                  fontFamily="'Poppins', sans-serif"
                  fontWeight="700"
                  sx={{
                    color: plan.highlighted ? 'inherit' : 'text.secondaryContrastText'
                  }}
                >
                  {plan.title}
                </Typography>

                {/* Price */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h3"
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: plan.highlighted ? 'inherit' : 'primary.main'
                    }}
                  >
                    {plan.price}
                  </Typography>
                </Box>

                {/* Features List */}
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    mb: 4,
                    flexGrow: 1
                  }}
                >
                  {plan.features.map((feature, featureIndex) => (
                    <Box
                      key={`feature-${featureIndex}`}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1.5
                      }}
                    >
                      <SvgIcon
                        fontSize="small"
                        sx={{
                          color: plan.highlighted ? 'inherit' : 'primary.main',
                          mt: 0.25,
                          flexShrink: 0
                        }}
                      >
                        <use href="#ico-check" />
                      </SvgIcon>
                      <Box sx={{ display: 'grid', rowGap: 0.5 }}>
                        <Typography
                          variant="textRegular"
                          fontWeight="500"
                          sx={{
                            color: plan.highlighted ? 'inherit' : 'text.primary'
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="textSmall"
                          sx={{
                            color: plan.highlighted ? 'inherit' : 'text.primary'
                          }}
                        >
                          {parse(feature.description)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* City Select */}
                {plan.cities && plan.cities.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      displayEmpty
                      value={selectedCities[index] || ''}
                      onChange={(e) =>
                        setSelectedCities((prev) => ({ ...prev, [index]: e.target.value }))
                      }
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <Box component="em" sx={{ fontStyle: 'normal', opacity: 0.6 }}>
                            {strings?.labels?.select_city_placeholder || 'Seleziona una città'}
                          </Box>
                        )
                      }
                      sx={{
                        color: plan.highlighted ? 'inherit' : 'text.primary',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: plan.highlighted ? 'rgba(255, 255, 255, 0.4)' : undefined
                        },
                        '& .MuiSvgIcon-root': {
                          color: plan.highlighted ? 'inherit' : undefined
                        }
                      }}
                    >
                      {plan.cities.map((city, cityIndex) => (
                        <MenuItem key={`city-${cityIndex}`} value={city.title}>
                          {city.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Acquista Button (Happa + product_id) OR CTA Button */}
                {isHappa && plan.product_id ? (
                  <Button
                    href={`/auth/products?productId=${plan.product_id}`}
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    color={plan.highlighted ? 'secondary' : 'primary'}
                    icon="cart"
                    fullWidth
                    disabled={!!plan.cities?.length && !selectedCities[index]}
                  >
                    {strings?.labels?.buy || 'Acquista'}
                  </Button>
                ) : (
                  plan?.button?.link?.url && (
                    <Button
                      href={plan.button.link.url}
                      variant={plan.highlighted ? 'contained' : 'outlined'}
                      color={plan.highlighted ? 'secondary' : 'primary'}
                      icon={plan.button.icon}
                      fullWidth
                      disabled={!!plan.cities?.length && !selectedCities[index]}
                    >
                      {plan.button.link.title}
                    </Button>
                  )
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default PricingGrid
